import React, { useState, useEffect } from 'react';
import { DollarSign, Calculator, TrendingUp, AlertCircle, Lightbulb } from 'lucide-react';
import { CostCalculator } from '@/lib/cost-calculator';
import type { CostCalculationInput, CostCalculationResult, UnitType, ProductType } from '@/types';
import { formatCurrency, formatNumber, formatWeight, getProfitMarginColor, getProfitMarginBgColor } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Input from '@/components/ui/input';
import Button from '@/components/ui/button';

export default function CostCalculatorComponent() {
  const [input, setInput] = useState<CostCalculationInput>({
    dimensions: { length: 20, width: 15, height: 10, unit: 'metric' },
    weight: { value: 500, unit: 'g' },
    purchasePriceCNY: 50,
    exchangeRate: 7.2,
    shippingPriceCNYPerKg: 12,
    sellingPriceUSD: 25,
    productType: 'standard',
    isPeakSeason: false,
    commissionRate: 15,
    fbaStorageRate: 2,
    otherFeesRate: 1,
    returnRate: 5,
    vatRate: 0
  });

  const [result, setResult] = useState<CostCalculationResult | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showOptimization, setShowOptimization] = useState(false);

  // 获取单位标签
  const getUnitLabels = (unit: UnitType) => {
    switch (unit) {
      case 'metric':
        return { length: 'cm', weight: 'g' };
      case 'imperial':
        return { length: 'in', weight: 'lb' };
      case 'imperial-oz':
        return { length: 'in', weight: 'oz' };
    }
  };

  // 验证输入
  const validateInput = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (input.dimensions.length <= 0) newErrors.length = '长度必须大于0';
    if (input.dimensions.width <= 0) newErrors.width = '宽度必须大于0';
    if (input.dimensions.height <= 0) newErrors.height = '高度必须大于0';
    if (input.weight.value <= 0) newErrors.weight = '重量必须大于0';
    if (input.purchasePriceCNY <= 0) newErrors.purchasePrice = '采购价格必须大于0';
    if (input.exchangeRate <= 0) newErrors.exchangeRate = '汇率必须大于0';
    if (input.sellingPriceUSD <= 0) newErrors.sellingPrice = '售价必须大于0';
    if (input.commissionRate < 0 || input.commissionRate > 100) {
      newErrors.commissionRate = '佣金率必须在0-100%之间';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 计算成本
  const calculateCost = async () => {
    if (!validateInput()) return;

    setIsCalculating(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      const calculationResult = CostCalculator.calculate(input);
      setResult(calculationResult);
    } catch (error) {
      console.error('计算错误:', error);
    } finally {
      setIsCalculating(false);
    }
  };

  // 自动计算
  useEffect(() => {
    const timer = setTimeout(() => {
      if (validateInput()) {
        const calculationResult = CostCalculator.calculate(input);
        setResult(calculationResult);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [input]);

  // 更新输入
  const updateInput = (field: string, value: any) => {
    setInput(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const updateDimension = (dimension: string, value: string) => {
    // 允许空值，这样用户可以删除所有数字重新输入
    if (value === '' || value === '-') {
      setInput(prev => ({
        ...prev,
        dimensions: {
          ...prev.dimensions,
          [dimension]: 0 // 临时设为0，但不影响计算，因为会在验证时被拦截
        }
      }));
      return;
    }

    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue > 0) {
      setInput(prev => ({
        ...prev,
        dimensions: {
          ...prev.dimensions,
          [dimension]: numValue
        }
      }));
    }
  };

  const updateWeight = (value: string) => {
    // 允许空值，这样用户可以删除所有数字重新输入
    if (value === '' || value === '-') {
      setInput(prev => ({
        ...prev,
        weight: {
          ...prev.weight,
          value: 0 // 临时设为0，但不影响计算，因为会在验证时被拦截
        }
      }));
      return;
    }

    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue > 0) {
      setInput(prev => ({
        ...prev,
        weight: {
          ...prev.weight,
          value: numValue
        }
      }));
    }
  };

  const unitLabels = getUnitLabels(input.dimensions.unit);

  // 盈亏平衡分析
  const breakEvenAnalysis = result ? CostCalculator.calculateBreakEven(input) : null;
  const optimizationSuggestions = result ? CostCalculator.generateOptimizationSuggestions(result) : [];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Calculator className="w-5 h-5 text-amazon-blue" />
            <span>成本分析计算器</span>
          </CardTitle>
          <CardDescription>
            全面的产品成本分析，包括采购、运输、FBA费用和各项杂费
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* 单位选择 */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-3 block">
              输入单位
            </label>
            <div className="flex space-x-4">
              {[
                { value: 'metric' as UnitType, label: '公制 (cm/g)' },
                { value: 'imperial' as UnitType, label: '英制 (in/lb)' },
                { value: 'imperial-oz' as UnitType, label: '英制 (in/oz)' }
              ].map((unit) => (
                <label key={unit.value} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="unit"
                    value={unit.value}
                    checked={input.dimensions.unit === unit.value}
                    onChange={(e) => {
                      const newUnit = e.target.value as UnitType;
                      updateInput('dimensions', { ...input.dimensions, unit: newUnit });
                      const weightUnit = newUnit === 'imperial' ? 'lb' : newUnit === 'imperial-oz' ? 'oz' : 'g';
                      updateInput('weight', { ...input.weight, unit: weightUnit });
                    }}
                    className="w-4 h-4 text-amazon-blue focus:ring-amazon-blue/20"
                  />
                  <span className="text-sm font-medium">{unit.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* 尺寸和重量 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-3 block">产品尺寸 ({unitLabels.length})</label>
              <div className="grid grid-cols-3 gap-3">
                <Input
                  label="长"
                  type="number"
                  step="0.01"
                  value={input.dimensions.length === 0 ? '' : input.dimensions.length}
                  onChange={(e) => updateDimension('length', e.target.value)}
                  error={errors.length}
                />
                <Input
                  label="宽"
                  type="number"
                  step="0.01"
                  value={input.dimensions.width === 0 ? '' : input.dimensions.width}
                  onChange={(e) => updateDimension('width', e.target.value)}
                  error={errors.width}
                />
                <Input
                  label="高"
                  type="number"
                  step="0.01"
                  value={input.dimensions.height === 0 ? '' : input.dimensions.height}
                  onChange={(e) => updateDimension('height', e.target.value)}
                  error={errors.height}
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-3 block">重量 ({unitLabels.weight})</label>
              <Input
                type="number"
                step="0.01"
                value={input.weight.value === 0 ? '' : input.weight.value}
                onChange={(e) => updateWeight(e.target.value)}
                error={errors.weight}
              />
            </div>
          </div>

          {/* 价格和费率 */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Input
              label="采购价格 (¥)"
              type="number"
              step="0.01"
              value={input.purchasePriceCNY}
              onChange={(e) => updateInput('purchasePriceCNY', parseFloat(e.target.value) || 0)}
              error={errors.purchasePrice}
            />
            <Input
              label="汇率 (USD/CNY)"
              type="number"
              step="0.01"
              value={input.exchangeRate}
              onChange={(e) => updateInput('exchangeRate', parseFloat(e.target.value) || 0)}
              error={errors.exchangeRate}
            />
            <Input
              label="船运价格 (¥/kg)"
              type="number"
              step="0.01"
              value={input.shippingPriceCNYPerKg}
              onChange={(e) => updateInput('shippingPriceCNYPerKg', parseFloat(e.target.value) || 0)}
            />
            <Input
              label="售价 (USD)"
              type="number"
              step="0.01"
              value={input.sellingPriceUSD}
              onChange={(e) => updateInput('sellingPriceUSD', parseFloat(e.target.value) || 0)}
              error={errors.sellingPrice}
            />
            <Input
              label="佣金率 (%)"
              type="number"
              step="0.01"
              value={input.commissionRate}
              onChange={(e) => updateInput('commissionRate', parseFloat(e.target.value) || 0)}
              error={errors.commissionRate}
            />
            <Input
              label="FBA仓储费率 (%)"
              type="number"
              step="0.01"
              value={input.fbaStorageRate}
              onChange={(e) => updateInput('fbaStorageRate', parseFloat(e.target.value) || 0)}
            />
          </div>

          {/* 其他费率 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="其他费用率 (%)"
              type="number"
              step="0.01"
              value={input.otherFeesRate}
              onChange={(e) => updateInput('otherFeesRate', parseFloat(e.target.value) || 0)}
            />
            <Input
              label="退货率 (%)"
              type="number"
              step="0.01"
              value={input.returnRate}
              onChange={(e) => updateInput('returnRate', parseFloat(e.target.value) || 0)}
            />
            <Input
              label="VAT费率 (%)"
              type="number"
              step="0.01"
              value={input.vatRate}
              onChange={(e) => updateInput('vatRate', parseFloat(e.target.value) || 0)}
            />
          </div>

          {/* 产品类型 */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-3 block">产品类型</label>
            <div className="flex space-x-4">
              {[
                { value: 'standard' as ProductType, label: '标准产品' },
                { value: 'apparel' as ProductType, label: '服装类商品' },
                { value: 'hazardous' as ProductType, label: '危险品' }
              ].map((type) => (
                <label key={type.value} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="productType"
                    value={type.value}
                    checked={input.productType === type.value}
                    onChange={(e) => updateInput('productType', e.target.value as ProductType)}
                    className="w-4 h-4 text-amazon-blue focus:ring-amazon-blue/20"
                  />
                  <span className="text-sm font-medium">{type.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="peakSeason"
              checked={input.isPeakSeason}
              onChange={(e) => updateInput('isPeakSeason', e.target.checked)}
              className="w-4 h-4 text-amazon-blue focus:ring-amazon-blue/20 rounded"
            />
            <label htmlFor="peakSeason" className="text-sm font-medium text-gray-700">
              旺季期间 (附加旺季费用)
            </label>
          </div>

          <Button onClick={calculateCost} loading={isCalculating} className="w-full" size="lg">
            分析成本和利润
          </Button>
        </CardContent>
      </Card>

      {/* 计算结果 */}
      {result && (
        <div className="animate-fade-in space-y-6">
          {/* 利润概览 */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <TrendingUp className="w-5 h-5 text-amazon-blue" />
                <span>利润分析</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className={`text-center p-4 rounded-lg border ${getProfitMarginBgColor(result.profit.profitMargin)}`}>
                  <div className={`text-2xl font-bold ${getProfitMarginColor(result.profit.profitMargin)}`}>
                    {formatCurrency(result.profit.grossProfitUSD)}
                  </div>
                  <div className="text-sm mt-1">毛利润</div>
                </div>
                <div className={`text-center p-4 rounded-lg border ${getProfitMarginBgColor(result.profit.profitMargin)}`}>
                  <div className={`text-2xl font-bold ${getProfitMarginColor(result.profit.profitMargin)}`}>
                    {formatNumber(result.profit.profitMargin, { decimals: 1, style: 'percent' })}
                  </div>
                  <div className="text-sm mt-1">利润率</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="text-2xl font-bold text-gray-700">
                    {formatCurrency(result.costs.adjustedTotalCostUSD)}
                  </div>
                  <div className="text-sm mt-1">总成本</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 成本明细 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>成本明细</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">采购成本</span>
                  <span className="font-medium">{formatCurrency(result.costs.purchaseCostUSD)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">运输成本</span>
                  <span className="font-medium">{formatCurrency(result.costs.shippingCostUSD)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">FBA费用</span>
                  <span className="font-medium">{formatCurrency(result.costs.fbaFeeUSD)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">平台佣金</span>
                  <span className="font-medium">{formatCurrency(result.costs.commissionUSD)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">仓储费用</span>
                  <span className="font-medium">{formatCurrency(result.costs.fbaStorageUSD)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">其他费用</span>
                  <span className="font-medium">{formatCurrency(result.costs.otherFeesUSD)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">VAT税费</span>
                  <span className="font-medium">{formatCurrency(result.costs.vatUSD)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">退货损失</span>
                  <span className="font-medium text-orange-600">
                    +{formatCurrency(result.costs.adjustedTotalCostUSD - result.costs.totalCostUSD)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-3 font-bold text-lg border-t-2 border-gray-200">
                  <span>调整后总成本</span>
                  <span className="text-red-600">{formatCurrency(result.costs.adjustedTotalCostUSD)}</span>
                </div>
              </CardContent>
            </Card>

            {/* 重量信息 */}
            <Card>
              <CardHeader>
                <CardTitle>重量信息</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">实际重量</span>
                  <span className="font-medium">{formatWeight(result.weights.actualWeightKg, 'kg')}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">体积重量</span>
                  <span className="font-medium">{formatWeight(result.weights.volumetricWeightKg, 'kg')}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600">计费重量</span>
                  <span className="font-medium text-blue-600">
                    {formatWeight(result.weights.billingWeightKg, 'kg')}
                  </span>
                </div>

                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="text-sm text-blue-800">
                    <strong>FBA费用分类:</strong> {result.fbaFee.sizeTier === 'small-standard' ? '小标准尺寸' :
                                                  result.fbaFee.sizeTier === 'large-standard' ? '大标准尺寸' :
                                                  result.fbaFee.sizeTier === 'small-oversize' ? '小大件' :
                                                  result.fbaFee.sizeTier === 'large-oversize' ? '大件' : '特殊超大件'}
                  </div>
                  <div className="text-sm text-blue-800 mt-1">
                    <strong>价格分段:</strong> {result.fbaFee.priceTier === 'low' ? '<$10' :
                                           result.fbaFee.priceTier === 'medium' ? '$10-$50' : '>$50'}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* 盈亏平衡分析 */}
          {breakEvenAnalysis && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <AlertCircle className="w-5 h-5 text-orange-500" />
                  <span>盈亏平衡分析</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-orange-50 rounded-lg border border-orange-200">
                    <div className="text-xl font-bold text-orange-700">
                      {formatCurrency(breakEvenAnalysis.breakEvenPriceUSD)}
                    </div>
                    <div className="text-sm text-orange-600 mt-1">盈亏平衡价格</div>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="text-xl font-bold text-green-700">
                      {formatCurrency(breakEvenAnalysis.recommendedPriceUSD)}
                    </div>
                    <div className="text-sm text-green-600 mt-1">推荐价格 (+20%)</div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="text-xl font-bold text-gray-700">
                      {formatCurrency(breakEvenAnalysis.breakEvenPriceCNY)}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">盈亏平衡价格 (¥)</div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="text-xl font-bold text-gray-700">
                      {formatCurrency(breakEvenAnalysis.recommendedPriceCNY)}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">推荐价格 (¥)</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* 优化建议 */}
          {optimizationSuggestions.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Lightbulb className="w-5 h-5 text-yellow-500" />
                  <span>优化建议</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {optimizationSuggestions.map((suggestion, index) => (
                    <div key={index} className={`p-4 rounded-lg border ${
                      suggestion.priority === 'high' ? 'bg-red-50 border-red-200' :
                      suggestion.priority === 'medium' ? 'bg-yellow-50 border-yellow-200' :
                      'bg-blue-50 border-blue-200'
                    }`}>
                      <div className="flex items-start space-x-3">
                        <div className={`w-2 h-2 rounded-full mt-2 ${
                          suggestion.priority === 'high' ? 'bg-red-500' :
                          suggestion.priority === 'medium' ? 'bg-yellow-500' :
                          'bg-blue-500'
                        }`}></div>
                        <div className="flex-1">
                          <div className="font-medium text-gray-800">{suggestion.suggestion}</div>
                          <div className="text-sm text-gray-600 mt-1">
                            预期节省: {formatCurrency(suggestion.potentialSavings)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}